import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useNavigation } from "@react-navigation/native";
import {
  Card,
  ControlledSwitch,
  FlatActionList,
  FlatActionListItem,
  FormLayout,
  TextField,
} from "@towersystems/roam-common-native";
import { LayoutNodeInput } from "@towersystems/roam-common/lib/generated-types";
import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Colors, Spacings, TouchableOpacity, View } from "react-native-ui-lib";
import Product from "../../../../utilities/data/model/Product";
import { ProductListItem } from "../ProductListItem";
import { Image } from "react-native";
import { ItemConfigType } from "../../../../utilities/layout";
import * as ImagePicker from "expo-image-picker";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export function LayoutNodeProductForm({
  item,
}: {
  item?: ItemConfigType | undefined;
}) {
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const navigation = useNavigation<any>();
  const database = useDatabase();
  const { control, setValue } = useFormContext<LayoutNodeInput>();

  const [product, setProduct] = useState<Product | undefined>();

  const watchedProductId = useWatch({
    control: control,
    name: "productId",
  });

  const watchedBase64Image = useWatch({
    control: control,
    name: "base64Image",
  });

  const watchedBackgroundColor = useWatch({
    control: control,
    name: "color",
  });

  const watchedLabelColour =
    useWatch({
      control: control,
      name: "labelColor",
    }) || Colors.white;

  useEffect(() => {
    if (!watchedProductId) {
      setProduct(undefined);
    } else {
      database
        .get<Product>("products")
        .find(watchedProductId)
        .then(setProduct)
        .catch(() => {
          setProduct(undefined);
        });
    }
  }, [watchedProductId]);

  function handleOpenProductList() {
    navigation.navigate("ProductListScreen");
  }

  const productMarkup = product ? (
    <ProductListItem product={product} action={handleOpenProductList} />
  ) : (
    <FlatActionList
      items={[
        {
          key: "product",
          title: "Assign new product",
          onPress: handleOpenProductList,
        },
      ]}
    />
  );

  const items: FlatActionListItem[] = [
    {
      key: "colour",
      title: "Choose Background Colour",
      subTitle: "Background colour will be used where no product image",
      componentRight: () => (
        <View>
          <TouchableOpacity
            style={{
              padding: Spacings.s4,
              width: 60,
              height: 60,
              borderRadius: 8,
              backgroundColor: watchedBackgroundColor,
              borderWidth: 2,
              borderColor: "#00000025",
            }}
            onPress={() =>
              navigation.navigate("ColorPickerScreen", {
                fieldName: "color",
              })
            }
          ></TouchableOpacity>
        </View>
      ),
    },
    {
      key: "labelColour",
      title: "Choose Label Colour",
      subTitle: "Set the colour of the label text on the quick key",
      componentRight: () => (
        <View>
          <TouchableOpacity
            style={{
              padding: Spacings.s4,
              width: 60,
              height: 60,
              borderRadius: 8,
              backgroundColor: watchedLabelColour,
              borderWidth: 2,
              borderColor: "#00000025",
            }}
            onPress={() =>
              navigation.navigate("ColorPickerScreen", {
                fieldName: "labelColor",
              })
            }
          ></TouchableOpacity>
        </View>
      ),
    },
    {
      key: "label",
      title: "Label",
      subTitle:
        "Set a custom label for this quick key. If empty, default name will be used.",
      componentRight: () => (
        <Controller
          name="label"
          render={({ field }) => (
            <TextField
              placeholder="Add a label..."
              value={field.value}
              onChangeText={field.onChange}
            />
          )}
        />
      ),
    },
    {
      key: "show_image",
      title: "Show Product Image",
      subTitle: "Product image will be used as background",
      componentRight: () => (
        <ControlledSwitch control={control} name="showImage" />
      ),
    },
  ];

  if (product) {
    if (!watchedBase64Image?.base64) {
      items.push({
        key: "pick_image",
        title: "Pick Image from Device",
        subTitle: "Select an image from your device to use for this quick key",
        onPress: async () => {
          const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: false,
            aspect: [1, 1],
            quality: 0.7,
            base64: true,
          });
          if (!result.canceled && result.assets && result.assets.length > 0) {
            const asset = result.assets[0];
            const imageName =
              asset.fileName || asset.uri?.split("/").pop() || "logo.jpg";
            setValue("base64Image", {
              base64: asset.base64 || "",
              imageName,
            });
          }
        },
      });
      if (strategyEnabled("generate_icon_quick_win")) {
        items.push({
          key: "generate_image",
          title: "Generate Button Icon with AI",
          disabled: !watchedProductId,
          subTitle:
            "Use AI to generate a background image for this product. Requires an AI image generation model to be configured in Settings. ",
          onPress: () =>
            navigation.navigate("GenerateAiIconRootScreen", {
              screen: "GenerateIconScreen",
              description: product.name,
            }),
        });
      }
    } else {
      items.push({
        key: "remove_image",
        destructive: true,
        title: "Remove Image",
        subTitle: "Remove the custom image set for this quick key",
        onPress: () => {
          setValue("base64Image", undefined, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
        componentRight: () => (
          <Image
            source={{
              uri: `data:image/png;base64,${watchedBase64Image?.base64}`,
            }}
            style={{ width: 40, height: 40, borderRadius: 4 }}
          />
        ),
      });
    }
  }

  if (
    item?.imageUrl &&
    // if watchedBase64 is null or has a value (not undefined)
    watchedBase64Image === undefined
  ) {
    items.push({
      key: "remove_saved_img",
      title: "Remove Quick Key Image",
      subTitle: "Remove any custom image set for this quick key",
      destructive: true,
      onPress: () => {
        // Remove the saved image URL - auto geneated form is incorrect type for api
        // @ts-ignore
        setValue("base64Image", null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
      componentRight: () => (
        <Image
          source={{
            uri: item?.imageUrl,
          }}
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
    });
  }

  return (
    <FormLayout>
      <Card title="Product" containerStyle={{ marginBottom: Spacings.s8 }}>
        {productMarkup}
      </Card>
      <Card title="Other" containerStyle={{ marginBottom: Spacings.s8 }}>
        <FlatActionList items={items} />
      </Card>
    </FormLayout>
  );
}
